/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccountClient, GET_AGGRGATED_VALUES } from '@nile/graphql';
import { GET_AVAILABLE_CREDITS } from '@nile/graphql/src/Query/client_management/partialPayments';
import { Card, TextStyle } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { CurrencyText } from '@nile/components';
import { FormattedMessage } from 'react-intl';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';

export const StatementCard = ({
  title,
  icon,
  loading,
  type,
  value
}: {
  title: string;
  icon?: string;
  loading: boolean;
  value: string | number;
  type: string;
}) => {
  return (
    <div className="fg1">
      <Card
        border="secondary"
        padding="sizeSmall"
        hideOnPrint
        loading={loading}
      >
        <div className="w350 h180">
          <span className="fx actr fw h-40 g10">
            <SVGIcon name={icon || 'BillableColor'} size="xl" />
            <TextStyle variation="strong">
              <FormattedMessage id={title} />
            </TextStyle>
          </span>
          <div className="fx actr jctr fw ch-40 ellipsis">
            <div className="f-24 font-weight-600">
              {type === 'currency' ? <CurrencyText value={value} /> : value}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

type StatementItem = {
  key: string;
  title: string;
  type?: 'currency' | 'number';
  icon?: string;
  value?: string | number;
};

export const StatementCards = ({
  isPreviewPage = false,
  conditions
}: {
  isPreviewPage?: boolean;
}) => {
  const { client: clientId } = useInvoiceStatementsStore();
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<StatementItem[]>(
    isPreviewPage
      ? [
          {
            key: 'totalOutstanding',
            title: 'invoice.statements.totalOutstanding',
            type: 'currency'
          },
          {
            key: 'credits',
            title: 'invoice.statements.cards.credits.title',
            type: 'currency'
          },
          {
            key: 'invoicesPastDue',
            title: 'invoice.statements.cards.invoicesPastDue.title',
            type: 'number'
          }
        ]
      : [
          {
            key: 'totalOutstanding',
            title: 'invoice.statements.totalOutstanding',
            type: 'currency'
          },
          {
            key: 'totalOverdue',
            title: 'invoice.statements.cards.totalOverdue.title',
            type: 'currency'
          },
          {
            key: 'clientsWithOverdue',
            title: 'invoice.statements.cards.clientsWithOverdue.title',
            type: 'number'
          },
          {
            key: 'invoicesPastDue',
            title: 'invoice.statements.cards.invoicesPastDue.title',
            type: 'number'
          }
        ]
  );

  const inputsByKey = useMemo(() => {
    return {
      totalOutstanding: {
        input: {
          entity: 'Invoice',
          metrics: [
            {
              metricFunction: 'SUM',
              attribute: 'amountDue',
              filterCondition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'statusEnum',
                    operator: 'notIncludes',
                    value: ['VOID', 'PAID', 'DRAFT']
                  },
                  clientId &&
                    isPreviewPage && {
                      attribute: 'client.accountId',
                      operator: 'is',
                      value: clientId
                    },
                  ...conditions
                ].filter(Boolean)
              }
            }
          ]
        }
      },
      totalOverdue: {
        input: {
          entity: 'Invoice',
          metrics: [
            {
              metricFunction: 'SUM',
              attribute: 'amountDue',
              filterCondition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'statusEnum',
                    operator: 'is',
                    value: 'OVERDUE'
                  },
                  ...conditions
                ].filter(Boolean)
              }
            }
          ]
        }
      },
      clientsWithOverdue: {
        input: {
          entity: 'Invoice',
          metrics: [
            {
              metricFunction: 'DISTINCT_COUNT',
              attribute: 'clientId',
              filterCondition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'statusEnum',
                    operator: 'is',
                    value: 'OVERDUE'
                  },
                  ...conditions
                ].filter(Boolean)
              }
            }
          ]
        }
      },
      invoicesPastDue: {
        input: {
          entity: 'Invoice',
          metrics: [
            {
              metricFunction: 'DISTINCT_COUNT',
              attribute: 'invoiceId',
              filterCondition: {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'statusEnum',
                    operator: 'is',
                    value: 'OVERDUE'
                  },
                  clientId &&
                    isPreviewPage && {
                      attribute: 'client.accountId',
                      operator: 'is',
                      value: clientId
                    },
                  ...conditions
                ].filter(Boolean)
              }
            }
          ]
        }
      },
      credits: {
        input: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'client.accountId',
              operator: 'is',
              value: clientId
            }
          ].filter(Boolean)
        }
      }
    } as Record<string, { input: Record<string, unknown> }>;
  }, [clientId, isPreviewPage, conditions]);

  type AggregatedValuesResponse = {
    data?: {
      getAggregatedValues?: {
        data?: Array<{
          data?: Array<{ value?: number }>;
        }>;
      };
    };
  };
  type AvailableCreditsResponse = {
    data?: {
      getAvailableCredits?: number | string;
    };
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const queries = items
      .map(
        (item) =>
          [item.key, inputsByKey[item.key]] as [
            string,
            { input: Record<string, unknown> } | undefined
          ]
      )
      .map(([key, variables]) => {
        if (key === 'credits') {
          return AccountClient.query({
            query: GET_AVAILABLE_CREDITS,
            variables,
            fetchPolicy: 'network-only'
          })
            .then((res: unknown) => {
              const r = res as AvailableCreditsResponse;
              const value =
                parseFloat((r?.data?.getAvailableCredits as any) ?? '0') || 0;
              return { key, value };
            })
            .catch(() => ({ key, value: 0 }));
        }
        return AccountClient.query({
          query: GET_AGGRGATED_VALUES,
          variables: variables as { input: Record<string, unknown> },
          fetchPolicy: 'network-only'
        })
          .then((res: unknown) => {
            const r = res as AggregatedValuesResponse;
            const value =
              r?.data?.getAggregatedValues?.data?.[0]?.data?.[0]?.value ?? 0;
            return { key, value };
          })
          .catch(() => ({ key, value: 0 }));
      });

    Promise.all(queries)
      .then((results: Array<{ key: string; value: number | string }>) => {
        if (!isMounted) return;
        const valueByKey = results.reduce<Record<string, number | string>>(
          (acc, { key, value }) => {
            acc[key] = value;
            return acc;
          },
          {}
        );
        setItems((prev) =>
          prev.map((it) => ({
            ...it,
            value: valueByKey[it.key] ?? it.value ?? 0
          }))
        );
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [inputsByKey]);

  return (
    <div className="fx gap-20 scroll">
      {items.map((item) => (
        <StatementCard
          key={item.title}
          title={item.title}
          type={item.type || 'number'}
          value={item.value ?? 0}
          loading={loading}
        />
      ))}
    </div>
  );
};
